import { useEffect, useState } from 'react'
import { Alert, Button, Form, ListGroup, Row, Card, Col } from 'react-bootstrap'
import { downloadExcel, downloadDepartmentSalaryExcel, getEmploymentTypes, updateEmploymentTaxes } from './SalaryAPI'
import WorkersModal from './WorkersModal'

export default function Excel({ isMobile, workers, workerCategories, jwt }) {
	const [showModal, setShowModal] = useState(false)
	const [selectedWorkers, setSelectedWorkers] = useState([])
	const [employmentTypes, setEmploymentTypes] = useState([])
	const [originalTaxes, setOriginalTaxes] = useState({})
	const [taxesChanged, setTaxesChanged] = useState(false)

	const [excelData, setExcelData] = useState({
		month: new Date().toISOString().split('T')[0],
		normalHours: 0,
		daysBid: 0,
		workers: [],
		employmentTaxes: {},
	})

	const percentToCoefficient = (percent) => (100 - percent) / 100
	const coefficientToPercent = (coefficient) => (1 - coefficient) * 100

	useEffect(() => {
		async function didMount() {
			const types = await getEmploymentTypes(jwt)
			if (types) {
				setEmploymentTypes(types)
				const original = {}
				const employmentTaxes = {}

				types.forEach(type => {
					original[type.id] = type.tax
					employmentTaxes[type.id] = type.tax
				})

				setOriginalTaxes(original)
				setExcelData(prev => ({ ...prev, employmentTaxes }))
			}
		}
		didMount()
	}, [jwt])

	useEffect(() => {
		const changed = employmentTypes.some(type => {
			const original = originalTaxes[type.id]
			return original !== undefined && Math.abs(original - type.tax) > 0.0001
		})
		setTaxesChanged(changed)
	}, [employmentTypes, originalTaxes])

	const handleModalShow = () => setShowModal(true)
	const handleWorkerSelect = workers => {
		setSelectedWorkers(workers)
		setShowModal(false)
		setExcelData(prev => ({ ...prev, workers }))
	}

	const handleTaxPercentChange = (id, percent) => {
		const numPercent = parseFloat(percent)
		if (isNaN(numPercent) || numPercent < 0 || numPercent > 100) return

		const coefficient = percentToCoefficient(numPercent)

		setEmploymentTypes(prev => prev.map(type =>
			type.id === id ? { ...type, tax: coefficient } : type
		))

		setExcelData(prev => ({
			...prev,
			employmentTaxes: { ...prev.employmentTaxes, [id]: coefficient }
		}))
	}

	const handleSaveTaxes = async () => {
		if (!taxesChanged) return

		const taxesToUpdate = employmentTypes
			.filter(type => {
				const original = originalTaxes[type.id]
				return original !== undefined && Math.abs(original - type.tax) > 0.0001
			})
			.map(type => ({ id: type.id, tax: type.tax }))

		const success = await updateEmploymentTaxes(jwt, taxesToUpdate)
		if (success) {
			const updatedOriginal = { ...originalTaxes }
			taxesToUpdate.forEach(item => {
				updatedOriginal[item.id] = item.tax
			})
			setOriginalTaxes(updatedOriginal)
			setTaxesChanged(false)
		}
	}

	const handleResetTaxes = () => {
		setEmploymentTypes(prev => prev.map(type => {
			const original = originalTaxes[type.id]
			if (original !== undefined) {
				return { ...type, tax: original }
			}
			return type
		}))

		setExcelData(prev => ({
			...prev,
			employmentTaxes: { ...originalTaxes }
		}))
		setTaxesChanged(false)
	}

	const downloadExcelFile = async () => {
		const workersIds = excelData.workers.map(worker => worker.id)
		const data = {
			...excelData,
			workers: workersIds,
			daysBid: excelData.daysBid ?? 0
		}

		if (data.daysBid == null) data.daysBid = 0

		await downloadExcel(jwt, data)
	}

	const handleSubmit = event => {
		event.preventDefault()
		const form = event.currentTarget
		if (form.checkValidity() === false) {
			event.stopPropagation()
		}
		downloadExcelFile()
	}

	const downloadSpecialExcelFile = async () => {
		const workersIds = excelData.workers.map(worker => worker.id)
		const data = {
			...excelData,
			workers: workersIds,
			daysBid: excelData.daysBid ?? 0
		}

		if (data.daysBid == null) data.daysBid = 0

		await downloadDepartmentSalaryExcel(jwt, data)
	}

	const handleClickDowndloadSpecialWorks = event => {
		event.preventDefault()
		const form = event.currentTarget
		if (form.checkValidity() === false) {
			event.stopPropagation()
		}
		downloadSpecialExcelFile()
	}

	return (
		<div>
			<h1 style={{ textAlign: 'center' }}>Excel</h1>
			<Form onSubmit={handleSubmit}>
				<Card className="mb-4">
					<Card.Header>
						<h5 className="mb-0">Налоги по типам трудоустройства</h5>
					</Card.Header>
					<Card.Body>
						<Row>
							{employmentTypes.map(type => {
								const percentValue = coefficientToPercent(type.tax)
								return (
									<Col md={4} key={type.id} className="mb-3">
										<Form.Group>
											<Form.Label>{type.title}</Form.Label>
											<div className="input-group">
												<Form.Control
													type="number"
													step="0.1"
													min="0"
													max="100"
													value={percentValue.toFixed(1)}
													onChange={(e) => handleTaxPercentChange(type.id, e.target.value)}
												/>
												<span className="input-group-text">%</span>
											</div>
										</Form.Group>
									</Col>
								)
							})}
						</Row>

						<div className="d-flex gap-2 mt-3">
							<Button
								variant="primary"
								onClick={handleSaveTaxes}
								disabled={!taxesChanged}
							>
								Сохранить
							</Button>
							<Button
								variant="outline-secondary"
								onClick={handleResetTaxes}
								disabled={!taxesChanged}
							>
								Сбросить
							</Button>
						</div>
					</Card.Body>
				</Card>

				<Form.Group className='mb-3'>
					<Form.Label htmlFor='fineDate'>Дата</Form.Label>
					<Form.Control
						required
						type='date'
						id='fineDate'
						onChange={e => setExcelData(prev => ({ ...prev, month: e.target.value }))}
					/>
				</Form.Group>

				<Form.Group className='mb-3'>
					<Form.Label htmlFor='normOfHours'>Норма часов</Form.Label>
					<Form.Control
						required
						type='number'
						id='normOfHours'
						min='0'
						value={excelData.normalHours}
						onChange={e => setExcelData(prev => ({ ...prev, normalHours: Number(e.target.value) }))}
					/>
				</Form.Group>

				<WorkersModal
					isMobile={isMobile}
					categories={workerCategories}
					show={showModal}
					onHide={() => setShowModal(false)}
					workers={workers}
					onSelect={handleWorkerSelect}
					excludedCategoryId={13}
				/>

				<div>
					<h3>Сотрудники:</h3>
					<Button className='mb-3' onClick={handleModalShow}>
						Редактировать
					</Button>
					<ListGroup>
						{selectedWorkers.length > 0 ? (
							<ul style={!isMobile ? { maxHeight: '450px', overflowY: 'auto' } : {}}>
								{selectedWorkers.map(worker => (
									<li className='mb-1' key={worker.id}>
										{`${worker.firstName} ${worker.secondName} ${worker.thirdName}`}
									</li>
								))}
							</ul>
						) : (
							<Alert variant='danger'>Не выбрано</Alert>
						)}
					</ListGroup>
				</div>
				<Row>
					<Button
						disabled={!excelData || excelData.month == '' || selectedWorkers.length <= 0}
						variant={'success'}
						style={{ width: '100%' }}
						type='submit'
					>
						Скачать
					</Button>
					<Button
						disabled={!excelData || excelData.month == '' || selectedWorkers.length <= 0}
						variant={'success'}
						style={{ width: '100%' }}
						onClick={handleClickDowndloadSpecialWorks}>
						Скачать c учётом отправок
					</Button>
				</Row>
			</Form>
		</div>
	)
}